import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = 'https://7b3t8n3es9.execute-api.us-east-1.amazonaws.com/prod';

interface EditProfileProps {
  userId: string;
  authToken: string;
  currentUser: {
    firstName: string;
    lastName: string;
  };
  onBack: () => void;
  onSave: (updatedUser: { firstName: string; lastName: string }) => void;
  onLogout: () => void;
  onDeleteAccount: () => void;
}

const EditProfile: React.FC<EditProfileProps> = ({ userId, authToken, currentUser, onBack, onSave, onLogout, onDeleteAccount }) => {
  const [form, setForm] = useState({
    firstName: currentUser.firstName || '',
    lastName: currentUser.lastName || '',
    zipCode: '',
    birthday: '',
  });
  const [showName, setShowName] = useState(true);
  const [shareJournal, setShareJournal] = useState(true);
  const [loading, setLoading] = useState(false);
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API_URL}/users?userId=${userId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.data) {
        setForm({
          firstName: res.data.firstName || '',
          lastName: res.data.lastName || '',
          zipCode: res.data.zipCode || '',
          birthday: res.data.birthday || '',
        });
        setShowName(res.data.showName !== false);
        setShareJournal(res.data.shareJournal !== false);
      }
    } catch (e) {
      console.log('Could not fetch profile');
    }
  };

  const validateBirthday = (value: string) => {
    if (!value) return true;
    const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
    return regex.test(value);
  };

  const handleShareJournalToggle = async (value: boolean) => {
    setShareJournal(value);
    setSavingPrivacy(true);
    try {
      await axios.put(`${API_URL}/users`, {
        userId,
        action: 'updateShareJournal',
        shareJournal: value,
      }, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
    } catch (e) {
      console.error('Failed to save journal sharing setting');
    }
    setSavingPrivacy(false);
  };

  const handlePrivacyToggle = async (value: boolean) => {
    setShowName(value);
    setSavingPrivacy(true);
    try {
      await axios.put(`${API_URL}/users`, {
        userId,
        action: 'updatePrivacy',
        showName: value,
      }, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
    } catch (e) {
      console.error('Failed to save privacy setting');
    }
    setSavingPrivacy(false);
  };

  const handleDownloadData = async () => {
    try {
      const [userRes, journalRes, wishlistRes] = await Promise.all([
        axios.get(`${API_URL}/users?userId=${userId}`, { headers: { Authorization: `Bearer ${authToken}` } }),
        axios.get(`${API_URL}/journal?userId=${userId}`, { headers: { Authorization: `Bearer ${authToken}` } }),
        axios.get(`${API_URL}/wishlist?userId=${userId}`, { headers: { Authorization: `Bearer ${authToken}` } }),
      ]);
      const data = {
        exportedAt: new Date().toISOString(),
        profile: userRes.data,
        journal: journalRes.data?.entries || journalRes.data,
        wishlist: wishlistRes.data?.items || wishlistRes.data,
      };
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'scentd-my-data.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('Could not download data. Please try again.');
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/users`, {
        data: { userId },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      onDeleteAccount();
    } catch (e) {
      setError('Could not delete account. Please try again.');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleSave = async () => {
    if (form.birthday && !validateBirthday(form.birthday)) {
      setError('Please enter birthday as MM/DD/YYYY');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.put(`${API_URL}/users`, {
        userId,
        ...form,
      }, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      onSave({ firstName: form.firstName, lastName: form.lastName });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-cream px-6 py-10 pb-24 md:pb-10">
      <button onClick={onBack} className="text-stone text-sm mb-8 block">← Back</button>
      <h2 className="font-serif text-3xl text-charcoal mb-2">Edit Profile</h2>
      <p className="text-stone-light text-sm mb-10">Update your personal details</p>

      <div className="space-y-5 max-w-sm">
        <div>
          <label className="block text-xs tracking-widest uppercase text-charcoal-light mb-2">First Name</label>
          <input
            type="text"
            value={form.firstName}
            onChange={e => setForm({ ...form, firstName: (e.target as HTMLInputElement).value })}
            className="w-full bg-white border border-cream-dark rounded-xl px-4 py-3 text-charcoal placeholder-stone-light focus:outline-none focus:border-stone transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs tracking-widest uppercase text-charcoal-light mb-2">Last Name</label>
          <input
            type="text"
            value={form.lastName}
            onChange={e => setForm({ ...form, lastName: (e.target as HTMLInputElement).value })}
            className="w-full bg-white border border-cream-dark rounded-xl px-4 py-3 text-charcoal placeholder-stone-light focus:outline-none focus:border-stone transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs tracking-widest uppercase text-charcoal-light mb-2">Zip Code</label>
          <input
            type="text"
            value={form.zipCode}
            onChange={e => setForm({ ...form, zipCode: (e.target as HTMLInputElement).value })}
            placeholder="10001"
            className="w-full bg-white border border-cream-dark rounded-xl px-4 py-3 text-charcoal placeholder-stone-light focus:outline-none focus:border-stone transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs tracking-widest uppercase text-charcoal-light mb-2">Birthday</label>
          <input
            type="text"
            value={form.birthday}
            onChange={e => setForm({ ...form, birthday: (e.target as HTMLInputElement).value })}
            placeholder="MM/DD/YYYY"
            className={`w-full bg-white border rounded-xl px-4 py-3 text-charcoal placeholder-stone-light focus:outline-none transition-colors ${
              form.birthday && !validateBirthday(form.birthday)
                ? 'border-red-300 focus:border-red-400'
                : 'border-cream-dark focus:border-stone'
            }`}
          />
          {form.birthday && !validateBirthday(form.birthday) && (
            <p className="text-red-400 text-xs mt-1">Please use MM/DD/YYYY format</p>
          )}
          <p className="text-stone-light text-xs mt-1">Enter as MM/DD/YYYY — used for your zodiac profile</p>
        </div>
      </div>

      {error && <p className="text-red-400 text-sm mt-4">{error}</p>}

      {/* Privacy settings */}
      <div className="mt-10 pt-8 border-t border-cream-dark max-w-sm">
        <h3 className="font-serif text-xl text-charcoal mb-1">Privacy</h3>
        <p className="text-stone-light text-sm mb-6">Control what others see on your profile</p>
        <div className="space-y-3">
          <div className="bg-white border border-cream-dark rounded-xl px-5 py-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-charcoal">Show my name</p>
              <p className="text-xs text-stone-light mt-0.5">When off, others only see your member number on your card</p>
            </div>
            <button
              onClick={() => handlePrivacyToggle(!showName)}
              disabled={savingPrivacy}
              className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ml-4 ${showName ? 'bg-charcoal' : 'bg-cream-dark'}`}
            >
              <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${showName ? 'left-6' : 'left-1'}`} />
            </button>
          </div>
          <div className="bg-white border border-cream-dark rounded-xl px-5 py-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-charcoal">Share journal with followers</p>
              <p className="text-xs text-stone-light mt-0.5 leading-relaxed">
                Your approved followers will see which fragrances you've rated as Liked or All-Time Fave, and what you have in common.
              </p>
              <p className="text-xs text-stone-light mt-1.5 leading-relaxed">
                <span className="text-charcoal">Never visible to followers:</span> your written notes, wear logs, and dates.
              </p>
            </div>
            <button
              onClick={() => handleShareJournalToggle(!shareJournal)}
              disabled={savingPrivacy}
              className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ml-4 ${shareJournal ? 'bg-charcoal' : 'bg-cream-dark'}`}
            >
              <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${shareJournal ? 'left-6' : 'left-1'}`} />
            </button>
          </div>
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={loading || (form.birthday !== '' && !validateBirthday(form.birthday))}
        className="mt-8 bg-charcoal text-cream px-8 py-4 text-sm tracking-widest uppercase hover:bg-charcoal-light transition-colors disabled:opacity-50"
      >
        {loading ? 'Saving...' : saved ? 'Saved ✓' : 'Save changes'}
      </button>

      <div className="mt-12 pt-8 border-t border-cream-dark max-w-sm space-y-4">
        <button onClick={onLogout} className="text-stone text-sm hover:text-charcoal transition-colors">
          Log out
        </button>

        <div className="flex items-center gap-6">
          <button onClick={handleDownloadData} className="text-stone text-sm hover:text-charcoal transition-colors">
            Download my data
          </button>
          <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="text-stone text-sm hover:text-charcoal transition-colors">
            Privacy policy
          </a>
        </div>

        <div className="pt-2 border-t border-cream-dark">
        {!showDeleteConfirm ? (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="text-red-400 text-sm hover:text-red-600 transition-colors"
          >
            Delete my account
          </button>
        ) : (
          <div className="bg-white border border-red-200 rounded-xl p-4">
            <p className="text-charcoal text-sm mb-1">Are you sure?</p>
            <p className="text-stone-light text-xs mb-4">This will permanently delete your account, journal, and wishlist. This cannot be undone.</p>
            <div className="flex gap-3">
              <button
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="bg-red-500 text-white text-xs px-4 py-2 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Yes, delete everything'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="text-stone text-xs px-4 py-2 hover:text-charcoal transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default EditProfile;